import React, { useEffect, useState } from "react";
import { Check, Crown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import useRazorpayCheckout from "@/lib/useRazorpayCheckout";
import PaymentSummaryDialog from "@/components/payments/PaymentSummaryDialog";
import api from "@/lib/api";

const PLANS = [
  {
    id: "free", name: "Free", price: "₹0", duration: "forever",
    description: "Get listed on Pune Spa Guide — free forever.",
    features: [
      "Free Listing Website",
      "Basic profile page",
      "Contact info display",
      "Gallery: up to 3 images",
      "Showcase up to 3 spa services",
      "Inclusion in directory",
      "Call CTA (no inquiry / booking)",
      "Standard support",
    ],
    cta: "Get Started",
    style: "white",
  },
  {
    id: "featured", name: "Featured", price: "₹999", priceAsterisk: true, duration: "per month",
    description: "Stand out with a Featured badge and top placement.",
    features: [
      "Everything in Free",
      "Featured badge",
      "Top placement in listings",
      "Gallery: up to 5 images",
      "Showcase up to 5 spa services",
      "Inquiry form (lead capture)",
      "Priority support",
    ],
    cta: "Upgrade to Featured",
    popular: true,
    style: "gold",
  },
  {
    id: "premium", name: "Premium", price: "₹2499", priceAsterisk: true, duration: "per month",
    description: "Maximum reach with homepage placement, WhatsApp CTA, and online booking.",
    features: [
      "Everything in Featured",
      "Homepage placement",
      "Premium crown badge",
      "Priority position",
      "WhatsApp CTA",
      "Online booking module",
      "Gallery: up to 10 images",
      "Showcase up to 10 spa services",
      "Lead form & analytics",
      "Dedicated support",
    ],
    cta: "Upgrade to Premium",
    style: "dark",
  },
];

export default function Plans() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sub, setSub] = useState(null);   // owner's live subscription snapshot
  React.useEffect(() => { document.title = "Pricing & Plans - Pune Spa Guide"; }, []);

  // Fetch the owner's current subscription so we can disable/label the CTA
  // buttons appropriately. Users on an active paid plan cannot switch mid-cycle.
  useEffect(() => {
    if (!user || user.role !== "owner") { setSub(null); return; }
    let cancelled = false;
    api.get("/owner/subscription")
      .then((r) => { if (!cancelled) setSub(r.data.subscription); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Razorpay checkout — success handler routes the owner to their dashboard
  // so they immediately see the new plan reflected in the SubscriptionStatusCard.
  const { open: openCheckout, loading: paying, config: rzpConfig } = useRazorpayCheckout({
    onSuccess: () => nav("/dashboard"),
  });

  // Two-step checkout: click Upgrade → show PaymentSummaryDialog (subtotal +
  // GST + total) → user confirms → Razorpay modal opens. Keeps the tax
  // breakdown transparent as required by Indian ecommerce norms.
  const [summaryPlan, setSummaryPlan] = useState(null);   // "featured" | "premium" | null
  const summaryPricing = summaryPlan && rzpConfig?.plans?.[summaryPlan]
    ? rzpConfig.plans[summaryPlan]
    : null;

  // ------------------------------------------------------------------------
  // Derive per-card CTA state.
  //   * lockedByCycle → owner is on an active paid plan; all paid-plan cards
  //     are disabled until the current cycle ends.
  //   * isCurrentPlan → the current plan gets a different label ("Current Plan").
  //   * showAsRenew → same plan when subscription is EXPIRED — offer "Renew" copy.
  // ------------------------------------------------------------------------
  const currentPlan = sub?.plan || (user?.role === "owner" ? "free" : null);
  const cycleActive = sub && sub.is_paid && ["active", "expiring_soon"].includes(sub.status);

  const ctaFor = (planId) => {
    if (planId === "free") return { label: "Get Started", disabled: false, kind: "free" };
    if (!user || user.role !== "owner") {
      // Public visitor / non-owner
      return { label: planId === "premium" ? "Upgrade to Premium" : "Upgrade to Featured", disabled: false, kind: "upgrade" };
    }
    if (currentPlan === planId && cycleActive) {
      return { label: "Current Plan", disabled: true, kind: "current" };
    }
    if (currentPlan === planId && !cycleActive) {
      // Same plan but expired — this becomes a Renew action
      return { label: `Renew ${planId === "premium" ? "Premium" : "Featured"}`, disabled: false, kind: "renew" };
    }
    if (cycleActive) {
      // Different paid plan attempted while another is active — block.
      return { label: "Locked until cycle ends", disabled: true, kind: "locked" };
    }
    return { label: planId === "premium" ? "Upgrade to Premium" : "Upgrade to Featured", disabled: false, kind: "upgrade" };
  };

  const handleClick = (id) => {
    if (id === "free") {
      window.location.href = user ? "/dashboard" : "/register";
      return;
    }
    // Paid plans → require login as an owner first.
    if (!user) {
      toast.info("Please log in as a spa owner to upgrade.");
      nav("/login", { state: { from: "/plans" } });
      return;
    }
    if (user.role !== "owner") {
      toast.error("Only spa owners can purchase a paid plan.");
      return;
    }
    // Frontend guard (server also enforces this via HTTP 409).
    const cta = ctaFor(id);
    if (cta.disabled) {
      if (cta.kind === "current") {
        toast.info("You're already on this plan for the current billing cycle.");
      } else if (cta.kind === "locked") {
        const expiryStr = sub?.expiry_date ? new Date(sub.expiry_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "the end of the cycle";
        toast.info(`Plan changes are locked until your current cycle ends on ${expiryStr}.`);
      }
      return;
    }
    // Open the payment summary dialog first — user reviews subtotal/GST/total,
    // then clicks "Proceed to Pay" which opens the Razorpay modal.
    setSummaryPlan(id);
  };

  // Called from the summary dialog's "Proceed to Pay" button.
  const proceedFromSummary = () => {
    if (!summaryPlan) return;
    openCheckout({
      plan: summaryPlan,
      prefill: { name: user.name, email: user.email, contact: user.phone },
    });
    setSummaryPlan(null);   // dismiss the summary; Razorpay modal takes over
  };
  return (
    <div data-testid="plans-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center max-w-4xl mx-auto mb-12">
        <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Pricing</div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-[2.5rem] lg:text-5xl text-[#111827] mt-2 lg:whitespace-nowrap">Choose the right plan for your spa</h1>
        <p className="mt-3 text-base text-[#4B5563]">Start free and upgrade anytime. No hidden charges.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {PLANS.map((p) => {
          const cta = ctaFor(p.id);
          const isCurrentActive = cta.kind === "current";
          const isLocked = cta.kind === "locked";
          return (
          <div
            key={p.id}
            data-testid={`plan-card-${p.id}`}
            className={`relative rounded-3xl p-8 ${
              p.style === "gold" ? "bg-white border-2 border-[#C9A86A] shadow-[0_18px_50px_rgba(201,168,106,0.18)]" :
              p.style === "dark" ? "bg-[#111827] text-white" :
              "bg-white border border-[#E7DDCF]"
            }`}
          >
            {p.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#C9A86A] text-[#111827] text-xs font-medium">Most Popular</div>
            )}
            {isCurrentActive && (
              <div className="absolute -top-3 right-4 px-3 py-1 rounded-full bg-[#DCFCE7] text-[#065F46] text-xs font-medium border border-[#86EFAC]" data-testid={`plan-current-badge-${p.id}`}>
                Your Current Plan
              </div>
            )}
            <div className="flex items-center gap-2">
              {p.id === "premium" && <Crown size={18} className="text-[#C9A86A]"/>}
              {p.id === "featured" && <Sparkles size={18} className="text-[#C9A86A]"/>}
              <h3 className="font-heading text-2xl">{p.name}</h3>
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-4xl font-medium">
                {p.price}
                {p.priceAsterisk && (
                  <sup className={`text-lg font-normal ml-0.5 ${p.style === "dark" ? "text-[#C9A86A]" : "text-[#C9A86A]"}`} aria-label="see footnote">*</sup>
                )}
              </span>
              <span className={`text-sm ${p.style === "dark" ? "text-white/70" : "text-[#4B5563]"}`}>/ {p.duration}</span>
            </div>
            <p className={`mt-2 text-sm ${p.style === "dark" ? "text-white/80" : "text-[#4B5563]"}`}>{p.description}</p>
            <ul className="mt-6 space-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check size={16} className={p.style === "dark" ? "text-[#C9A86A] mt-0.5" : "text-[#1F4E3D] mt-0.5"}/> {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => handleClick(p.id)}
              disabled={cta.disabled || (paying && p.id !== "free")}
              data-testid={p.id === "free" ? "plan-free-cta" : `plan-upgrade-${p.id}-button`}
              className={`mt-8 w-full h-12 transition-colors ${
                cta.disabled
                  ? (isCurrentActive
                      ? "bg-[#DCFCE7] text-[#065F46] hover:bg-[#DCFCE7] border border-[#86EFAC] cursor-not-allowed"
                      : "bg-[#E5E7EB] text-[#4B5563] hover:bg-[#E5E7EB] cursor-not-allowed")
                  : p.id === "free" ? "bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white" :
                    p.style === "dark" ? "bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827]" :
                    "bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827]"
              }`}
            >
              {paying && p.id !== "free" ? "Opening…" : cta.label}
            </Button>
            {isLocked && (
              <div className="mt-2 text-[11px] text-center text-[#4B5563] leading-relaxed" data-testid={`plan-locked-note-${p.id}`}>
                Plan changes are locked until{" "}
                <strong>{sub?.expiry_date ? new Date(sub.expiry_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "cycle end"}</strong>.
              </div>
            )}
          </div>
        );})}
      </div>

      <div className="mt-12 text-center text-xs text-[#4B5563]">
        <span className="text-[#C9A86A] font-medium">*</span> Pricing in INR (₹). 18% GST charged at checkout. Secure payments powered by Razorpay.
      </div>

      {/* Payment summary dialog — opens on Upgrade click, shows subtotal + GST + total,
          then triggers the Razorpay modal when the user confirms. */}
      <PaymentSummaryDialog
        open={!!summaryPlan}
        onCancel={() => setSummaryPlan(null)}
        onConfirm={proceedFromSummary}
        plan={summaryPlan}
        pricing={summaryPricing}
        loading={paying}
      />
    </div>
  );
}
